import { useContext, useEffect, useState } from "react"
import { APIError } from "../../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { impossible } from "../../../app/Utils"
import { showError } from "../../notifications/notificationsSlice"
import { DocumentSectionHeader } from "../components/DocumentSectionHeader"
import { SectionDeleteContext } from "../DocumentContent"
import { updateDocSection } from "../documentsApi"
import { isDocEditable, sectionSelectors, selectDocument } from "../documentSlice"
import { DocumentSectionDeleteDialog } from "../utils/DeleteSectionDialog"
import {
    loadRiskAnalysisRecommendedActions,
    RecommendedAction,
    RiskAnalysisRecommendedActions,
} from "./riskAnalysisApi"
import {
    Box,
    Chip,
    CircularProgress,
    Divider,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import { Localized } from "@fluent/react"

type RiskLevel = 'CRITICAL' | 'MEDIUM' | 'LOW' | string

function riskLevelColor(level: RiskLevel): 'error' | 'warning' | 'success' | 'default' {
    switch (level?.toUpperCase()) {
        case 'CRITICAL': return 'error'
        case 'MEDIUM':   return 'warning'
        case 'LOW':      return 'success'
        default:         return 'default'
    }
}

const ThreatBox = ({ count, color, label }: {
    count: number
    color: 'error' | 'warning' | 'success'
    label: React.ReactNode
}) => {
    const borderColor = color === 'error' ? 'error.main' : color === 'warning' ? 'warning.main' : 'success.main'
    const bgColor    = color === 'error' ? 'error.50'   : color === 'warning' ? 'warning.50'   : 'success.50'
    return (
        <Paper variant="outlined" sx={{
            borderLeft: 4,
            borderColor,
            bgcolor: bgColor,
            px: 2,
            py: 1,
            minWidth: 110,
            textAlign: 'center',
        }}>
            <Typography variant="h5" fontWeight="bold" color={`${color}.main`}>{count}</Typography>
            <Typography variant="caption" color="text.secondary">{label}</Typography>
        </Paper>
    )
}

const HtmlCell = ({ html }: { html: string }) => (
    <div dangerouslySetInnerHTML={{ __html: html }} style={{ fontSize: '0.8125rem' }} />
)

export const RecommendedActionSection = (props: { sectionId: string }) => {
    const { sectionId } = props
    const dispatch = useAppDispatch()
    const section = useAppSelector(state => sectionSelectors.selectById(state, sectionId)) ?? impossible()
    const { header } = useAppSelector(selectDocument)?.doc ?? impossible()
    const { numeration } = section
    const isEditable = useAppSelector(isDocEditable)
    const deleteSection = useContext(SectionDeleteContext)

    const [error, setError] = useState<APIError | null>(null)
    const [del, setDel] = useState(false)
    const [deleting, setDeleting] = useState(false)

    const [data, setData] = useState<RiskAnalysisRecommendedActions | null>(null)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        const load = async () => {
            setLoading(true)
            try {
                const result = await dispatch(loadRiskAnalysisRecommendedActions({ docId: Number(header.id) })).unwrap()
                setData(result)
            } catch {
                dispatch(showError('error'))
            } finally {
                setLoading(false)
            }
        }
        load()
    }, [header.id])

    const handleApprove = async (newName: string) => {
        if (newName) {
            try {
                await dispatch(updateDocSection({ ...section, name: newName })).unwrap()
            } catch (error) {
                setError(error as APIError)
            }
        }
    }

    const handleDeleteClick = () => setDel(true)
    const handleConfirmDeleteClick = async () => {
        try {
            setDeleting(true)
            deleteSection(sectionId)
        } catch {
            dispatch(showError('error'))
        } finally {
            setDeleting(false)
        }
    }
    const handleCancelDelClick = () => setDel(false)

    const actions: RecommendedAction[] = (data?.recommendedActions as RecommendedAction[] | undefined) ?? []

    return <>
        <DocumentSectionHeader
            numeration={numeration}
            name={section.name}
            isEditable={isEditable}
            error={error}
            onSaveName={handleApprove}
            onDeleteClick={handleDeleteClick}
        />
        {del &&
            <DocumentSectionDeleteDialog
                deleting={deleting}
                hasSubSections={section.subSectionsIds.length > 0}
                onConfirm={handleConfirmDeleteClick}
                onCancel={handleCancelDelClick}
            />
        }
        <Divider />

        {loading
            ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={28} />
              </Box>
            : data && <>
                {/* Threats summary */}
                <Box sx={{ mt: 2, mb: 2 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                        <Localized id="risk-analysis-threats">Threats</Localized>
                    </Typography>
                    <Stack direction="row" spacing={2}>
                        <ThreatBox
                            count={data.threats.critical}
                            color="error"
                            label={<Localized id="risk-analysis-critical">Critical</Localized>}
                        />
                        <ThreatBox
                            count={data.threats.medium}
                            color="warning"
                            label={<Localized id="risk-analysis-medium">Medium</Localized>}
                        />
                        <ThreatBox
                            count={data.threats.low}
                            color="success"
                            label={<Localized id="risk-analysis-low">Low</Localized>}
                        />
                    </Stack>
                </Box>

                {/* Recommended actions table */}
                <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ '& th': { fontWeight: 600, whiteSpace: 'nowrap' } }}>
                                <TableCell sx={{ width: 40 }}><Localized id="lp">No</Localized></TableCell>
                                <TableCell><Localized id="risk-analysis-recommended-action">Recommended action</Localized></TableCell>
                                <TableCell><Localized id="risk-analysis-function">Function</Localized></TableCell>
                                <TableCell><Localized id="risk-analysis-potential-defect">Potential defect</Localized></TableCell>
                                <TableCell><Localized id="risk-analysis-potential-consequences">Consequences</Localized></TableCell>
                                <TableCell sx={{ width: 100 }}><Localized id="risk-analysis-risk-level">Risk level</Localized></TableCell>
                                <TableCell align="right" sx={{ width: 100 }}><Localized id="risk-analysis-rpn">RPN</Localized></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {actions.length === 0
                                ? <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                                        <Localized id="no-data">No data</Localized>
                                    </TableCell>
                                  </TableRow>
                                : actions.map((action, idx) => (
                                    <TableRow
                                        key={`${action.reasonId}-${idx}`}
                                        hover
                                        sx={{
                                            borderLeft: 3,
                                            borderColor: `${riskLevelColor(action.riskLevel)}.main`,
                                        }}
                                    >
                                        <TableCell>{idx + 1}</TableCell>
                                        <TableCell sx={{ verticalAlign: 'top', fontWeight: 500, fontSize: '1em', borderRight: '1px solid var(--gray-300)' }}>
                                            <HtmlCell html={action.recommendedAction} />
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 160, verticalAlign: 'top' }}>
                                            <HtmlCell html={action.functionName} />
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 160, verticalAlign: 'top' }}>
                                            <HtmlCell html={action.defectName} />
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 160, verticalAlign: 'top' }}>
                                            <HtmlCell html={action.consequenceName} />
                                        </TableCell>
                                        <TableCell sx={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                                            <Chip
                                                size="small"
                                                label={action.riskLevel}
                                                color={riskLevelColor(action.riskLevel)}
                                            />
                                        </TableCell>
                                        <TableCell align="right" sx={{ verticalAlign: 'top', fontWeight: 600 }}>
                                            {action.rpn}
                                        </TableCell>
                                    </TableRow>
                                ))
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
              </>
        }
    </>
}
